import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface StatsItem {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  description?: string;
}

interface StatsData {
  title?: string;
  subtitle?: string;
  items?: StatsItem[];
  layout?: 'row' | 'grid';
  columns?: number;
  animated?: boolean;
  animationDuration?: number;
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    value?: string;
    label?: string;
    description?: string;
  };
}

interface StatsEditorProps {
  formData: StatsData;
  setFormData: (data: StatsData) => void;
}

export const StatsEditor: React.FC<StatsEditorProps> = ({ formData, setFormData }) => {
  const data = formData;
  const items = data.items || [];
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  const updateItem = (index: number, updates: Partial<StatsItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...updates };
    setFormData({ ...data, items: updated });
  };

  const addItem = () => {
    setFormData({
      ...data,
      items: [...items, { label: 'New Stat', value: 0 }],
    });
  };

  const removeItem = (index: number) => {
    setFormData({
      ...data,
      items: items.filter((_, i) => i !== index),
    });
  };

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const updated = [...items];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setFormData({ ...data, items: updated });
  };

  const colorFields = getColorFieldsForComponent('stats', themeColors);

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Our Impact in Numbers"
          maxLength={200}
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Real results, real impact"
          maxLength={500}
        />
      </div>

      {/* Layout & Animation Options */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Display Options</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
            <select
              value={data.layout || 'row'}
              onChange={e => setFormData({ ...data, layout: e.target.value as 'row' | 'grid' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="row">Row (centered, wrapping)</option>
              <option value="grid">Grid (even columns)</option>
            </select>
          </div>

          {data.layout === 'grid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
              <select
                value={data.columns || 3}
                onChange={e => setFormData({ ...data, columns: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
              </select>
            </div>
          )}
        </div>

        {/* Animation Toggle */}
        <label className="flex items-center space-x-3 mt-4">
          <input
            type="checkbox"
            checked={data.animated !== false}
            onChange={e => setFormData({ ...data, animated: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Animate numbers</span>
            <p className="text-xs text-gray-500">
              Numbers count up from zero when scrolled into view
            </p>
          </div>
        </label>

        {data.animated !== false && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Animation Duration ({data.animationDuration || 2000}ms)
            </label>
            <input
              type="range"
              min={500}
              max={5000}
              step={100}
              value={data.animationDuration || 2000}
              onChange={e => setFormData({ ...data, animationDuration: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Fast (0.5s)</span>
              <span>Slow (5s)</span>
            </div>
          </div>
        )}
      </div>

      {/* Stat Items */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-medium text-gray-900">Stats ({items.length})</h3>
          <button
            type="button"
            onClick={addItem}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
          >
            <Plus size={16} />
            <span>Add Stat</span>
          </button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-3">
                {/* Move Button */}
                <div className="flex flex-col gap-1 pt-2">
                  <button
                    type="button"
                    onClick={() => moveItem(index, index - 1)}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <GripVertical size={14} />
                  </button>
                </div>

                <div className="flex-1 space-y-3">
                  {/* Value Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Prefix</label>
                      <input
                        type="text"
                        value={item.prefix || ''}
                        onChange={e => updateItem(index, { prefix: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        placeholder="$"
                        maxLength={10}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                      <input
                        type="number"
                        value={item.value}
                        onChange={e =>
                          updateItem(index, { value: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        placeholder="10000"
                        step="any"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Suffix</label>
                      <input
                        type="text"
                        value={item.suffix || ''}
                        onChange={e => updateItem(index, { suffix: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        placeholder="+, %, K"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  {/* Label */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                    <input
                      type="text"
                      value={item.label}
                      onChange={e => updateItem(index, { label: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Happy Customers"
                      maxLength={100}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Description <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={e => updateItem(index, { description: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Sub-text shown below the label"
                      maxLength={200}
                    />
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Remove stat"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">
                No stats added yet. Click &quot;Add Stat&quot; to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize section colors. Leave empty to use theme defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};
